import { TFunction } from 'i18next';
import {
  AddCampaignFormStringValues,
  AddCampaignFormValues,
  AddCampaignValidationFields,
  AddCampaignValueErrors,
  IVolumeDistributionAsString,
} from './AddCampaignForm.types';
import { isIsoDateStringOnOrAfterCompareDate, isValidIsoDate } from '@/common/utils/date/date';
import { ShowItemsObj } from './AddCampaignForm';

interface ICopiesContent {
  label: string;
  placeholder: string;
  secondaryLabel: string;
}

interface ICopies {
  campaignName: ICopiesContent;
  campaignSource: ICopiesContent;
  campaignTerm: ICopiesContent;
  campaignLaunchDate: ICopiesContent;
  campaignMedium: ICopiesContent;
  campaignContent: ICopiesContent;
  campaignEndDate: ICopiesContent;
  options: {
    cancel: string;
    submit: string;
  };
}

interface IGetCopies {
  t: TFunction;
}

export const getCopies = ({ t }: IGetCopies): ICopies => {
  return {
    campaignName: {
      label: t('add_campaign.campaign_name.label'),
      placeholder: t('add_campaign.campaign_name.placeholder'),
      secondaryLabel: `(${t('add_campaign.campaign_name.secondaryLabel')})`,
    },
    campaignSource: {
      label: t('add_campaign.campaign_source.label'),
      placeholder: t('add_campaign.campaign_source.placeholder'),
      secondaryLabel: `(${t('add_campaign.campaign_source.secondaryLabel')})`,
    },
    campaignTerm: {
      label: t('add_campaign.campaign_term.label'),
      placeholder: t('add_campaign.campaign_term.placeholder'),
      secondaryLabel: `(${t('add_campaign.campaign_term.secondaryLabel')})`,
    },
    campaignLaunchDate: {
      label: t('add_campaign.campaign_launch_date.label'),
      placeholder: t('add_campaign.campaign_launch_date.placeholder'),
      secondaryLabel: `(${t('add_campaign.campaign_launch_date.secondaryLabel')})`,
    },
    campaignMedium: {
      label: t('add_campaign.campaign_medium.label'),
      placeholder: t('add_campaign.campaign_medium.placeholder'),
      secondaryLabel: `(${t('add_campaign.campaign_medium.secondaryLabel')})`,
    },
    campaignContent: {
      label: t('add_campaign.campaign_content.label'),
      placeholder: t('add_campaign.campaign_content.placeholder'),
      secondaryLabel: `(${t('add_campaign.campaign_content.secondaryLabel')})`,
    },
    campaignEndDate: {
      label: t('add_campaign.campaign_end_date.label'),
      placeholder: t('add_campaign.campaign_end_date.placeholder'),
      secondaryLabel: `(${t('add_campaign.campaign_end_date.secondaryLabel')})`,
    },
    options: {
      cancel: t('add_campaign.options.cancel'),
      submit: t('add_campaign.options.submit'),
    },
  };
};

export const stringIsNotOnlySpaces = (stringToEvaluate: string): boolean => {
  return stringToEvaluate.trim().length > 0;
};

const isStringBiggerThan = (stringToEvaluate: string, amount: number): boolean => {
  return stringToEvaluate.length > amount;
};

interface IValuesConfig {
  t: TFunction;
}

// This fn will validate if the should validate is true and it will get the
// string from the form to compare it against the fn
// Volume distribution is not validated using this object as
// it is an array and it will break the structure of this object
export const addCampaignValidationConfig = ({ t }: IValuesConfig): AddCampaignValidationFields => {
  return {
    campaignName: {
      shouldValidate: true,
      validateFn: (newValue: string) =>
        isStringBiggerThan(newValue, 1) && checkCampaignValue(newValue) && stringIsNotOnlySpaces(newValue),
      validationError: t('add_campaign.campaign_name.error'),
    },

    campaignSource: {
      shouldValidate: true,
      validateFn: (newValue: string) =>
        isStringBiggerThan(newValue, 1) && checkCampaignValue(newValue) && stringIsNotOnlySpaces(newValue),
      validationError: t('add_campaign.campaign_source.error'),
    },
    campaignMedium: {
      shouldValidate: true,
      validateFn: (newValue: string) => isStringBiggerThan(newValue, 1) && checkCampaignValue(newValue),
      validationError: t('add_campaign.campaign_medium.error'),
    },
    campaignTerm: {
      shouldValidate: true,
      validateFn: (newValue: string) => checkCampaignValue(newValue),
      validationError: t('add_campaign.campaign_term.error'),
    },
    campaignContent: {
      shouldValidate: true,
      validateFn: (newValue: string) => checkCampaignValue(newValue),
      validationError: t('add_campaign.campaign_content.error'),
    },
    launchDate: {
      shouldValidate: true,
      validateFn: (newValue: string) => isValidIsoDate(newValue),
      validationError: t('add_campaign.campaign_launch_date.error'),
    },
    endDate: {
      shouldValidate: true,
      validateFn: (newValue: string, allValues: AddCampaignFormValues | undefined): boolean => {
        if (!allValues?.launchDate?.value) return true;
        if (!allValues?.endDate?.value) return true;
        return (
          isValidIsoDate(newValue) &&
          isIsoDateStringOnOrAfterCompareDate({ date: newValue, compareDate: allValues?.launchDate.value })
        );
      },
      validationError: t('add_campaign.campaign_end_date.error'),
    },
  };
};

// This fn will get the values from the config (ValuesConfig)
// and make the validation of the fields against its configuration
// if it should validate it will execute the validate Fn otherwise
// it will return undefined as there is not an error
const getValidateFieldError = (
  fieldName: string,
  allValues: AddCampaignFormValues,
  shouldValidate?: boolean,
  validateFn?: (value: string, allValues?: AddCampaignFormValues) => boolean,
  validateError?: string,
): string | undefined => {
  const field = allValues[fieldName as keyof typeof allValues];
  if (!shouldValidate || !validateFn) {
    return undefined;
  }
  if (validateFn(field?.value ?? '', allValues)) {
    return undefined;
  }
  return validateError ?? 'Field is mandatory';
};

export const validateForm = (
  values: AddCampaignFormValues,
  t: TFunction,
  getConfig: ({ t }: { t: TFunction }) => AddCampaignValidationFields,
): AddCampaignValueErrors => {
  const validationConfig = getConfig({ t });
  const valuesArray = Object.keys(values).map((key) => {
    const valueConfig = validationConfig[key as keyof typeof validationConfig];
    const errorString = getValidateFieldError(
      key,
      values,
      valueConfig?.shouldValidate,
      valueConfig?.validateFn,
      valueConfig?.validationError,
    );
    return {
      key,
      error: errorString,
    };
  });
  return valuesArray.reduce((acc, act) => {
    return {
      ...acc,
      [act.key]: act.error,
    };
  }, {});
};

export const transformCampaignFormStringValuesToCampaignFormValues = (
  formStringValues: AddCampaignFormStringValues,
): AddCampaignFormValues => {
  return {
    campaignName: {
      value: formStringValues.campaignName ?? '',
    },
    campaignSource: {
      value: formStringValues.campaignSource ?? '',
    },
    campaignTerm: {
      value: formStringValues.campaignTerm ?? '',
    },
    campaignMedium: {
      value: formStringValues.campaignMedium ?? '',
    },
    campaignContent: {
      value: formStringValues.campaignContent ?? '',
    },
    launchDate: {
      value: formStringValues.launchDate ?? '',
    },
    endDate: {
      value: formStringValues.endDate ?? '',
    },
  };
};

export const validateVolumeDistributionItem = (
  values: IVolumeDistributionAsString,
  t: TFunction,
): IVolumeDistributionAsString => {
  if (values.distributionDate === undefined && values.numberOfEnrollees === undefined) {
    return {
      distributionDate: undefined,
      numberOfEnrollees: undefined,
    };
  }
  let volumeError: string | undefined;
  const dateError: string | undefined = isValidIsoDate(values?.distributionDate ?? '')
    ? undefined
    : t('add_campaign.volume_distribution.error.distribution_date');
  try {
    const valueAsNumber = Number.parseInt(values.numberOfEnrollees ?? '');
    if (Number.isNaN(valueAsNumber) || valueAsNumber < 1) {
      volumeError = t('add_campaign.volume_distribution.error.enrollment');
    }
  } catch (e) {
    console.error(e);
  }
  return {
    numberOfEnrollees: volumeError,
    distributionDate: dateError,
  };
};

// checkCampaignValue to only allow alphanumeric, spaces, hyphens and underscores or empty string
export const checkCampaignValue = (value: string): boolean => {
  const regex = /^[a-zA-Z0-9_ -]*$/;
  return value.match(regex) !== null;
};

const defaultUIElements: ShowItemsObj = {
  all: false,
  volumeDistribution: false,
  dates: false,
  campaignInfo: false,
};

export const getUiElementsToShow = (showItems: Array<keyof ShowItemsObj>): ShowItemsObj => {
  const allItem = showItems.find((item) => item === 'all');

  return Object.keys(defaultUIElements).reduce((acc, act) => {
    if (showItems.includes(act as keyof ShowItemsObj) || allItem) {
      return {
        ...acc,
        [act]: true,
      };
    }
    return acc;
  }, defaultUIElements);
};
